import { UUID } from "@bitory-gmbh/nidavellir";
import { EntityId } from "@/shared/types/entity-id";
import type { WaterKind } from "@/services/backend/samples/types/water-sample";

/**
 * The backend subdomain the sample base data defaults are mounted under,
 * see `DefaultBaseDataCheckbox`.
 */
export const samplesBaseDataSubdomain = "samples";

/**
 * The `basedatakit.Default` keys under which a company marks the base data
 * a new sample of the kind is created from. Mirror the
 * `Default<Kind>BaseDataKey` constants of the backend.
 */
export const airMicrobialBaseDataKey = "samples.airMicrobial.baseData";
export const surfaceMicrobialBaseDataKey = "samples.surfaceMicrobial.baseData";
export const dustDensityBaseDataKey = "samples.dustDensity.baseData";
export const waterBaseDataKey = "samples.water.baseData";

/**
 * SampleBaseData holds the fields every sample base data shares, see
 * `basedatakit.Base` in the backend. The label identifies an instance for
 * the user and is unique per company.
 */
export interface SampleBaseData {
  id: UUID;
  companyId: UUID;
  label: string;
  createdAt: string;
  updatedAt: string;
}

/** The defaults a new air microbial sample set is created with. */
export interface AirMicrobialBaseData extends SampleBaseData {
  /** The unit of the germ count of the set. */
  unit: string;
  /** The unit of the germ count of the samples of the set. */
  sampleUnit: string;
  /** The volume of air taken for the samples of the set. */
  samplingVolume: number;
  /** Whether a dg18 sample is taken, besides the caso and malt samples. */
  dg18: boolean;
  /** The lab the samples are sent to; null leaves it to be chosen. */
  labId: EntityId;
  /** The device the samples are taken with; null leaves it to be chosen. */
  deviceId: EntityId;
}

/** The defaults a new surface microbial sample set is created with. */
export interface SurfaceMicrobialBaseData extends SampleBaseData {
  /** The unit of the germ count of the set. */
  unit: string;
  /** The unit of the germ count of the samples of the set. */
  sampleUnit: string;
  /** The germ count from which on a set is judged as a warning. */
  warningFrom: number;
  /**
   * The germ count above which a set is judged as critical; 0 means no
   * thresholds are set.
   */
  criticalAbove: number;
  /** Whether a dg18 sample is taken, besides the caso and malt samples. */
  dg18: boolean;
  /** The lab the samples are sent to; null leaves it to be chosen. */
  labId: EntityId;
}

/** The defaults a new dust density sample is created with. */
export interface DustDensityBaseData extends SampleBaseData {
  unit: string;
  referenceValue: string;
  /** The lab the sample is sent to; null leaves it to be chosen. */
  labId: EntityId;
}

/** The defaults a new water sample is created with. */
export interface WaterBaseData extends SampleBaseData {
  /**
   * The measurements a new sample receives. Every template is defined for
   * one water kind; a sample receives the templates of its kind.
   */
  measurements: MeasurementTemplate[];
  /** The lab the sample is sent to; null leaves it to be chosen. */
  labId: EntityId;
}

/**
 * A measurement a new water sample of the water kind is created with. The
 * order is unique per water kind and orders the measurements of a sample.
 */
export interface MeasurementTemplate {
  waterKind: WaterKind;
  order: number;
  name: string;
  referenceValue: string;
  unit: string;
}
